<script setup lang="ts">
import {
	onMounted,
	ref,
	computed,
	onBeforeUnmount,
} from 'vue';

import CloseButton from '@zyro-inc/site-modules/components/CloseButton.vue';
import IconBack from '@zyro-inc/site-modules/components/icons/IconBack.vue';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';

const { closeEcommerceModal } = useEcommerceModal();

const props = withDefaults(defineProps<{
	minHeight?: string;
	maxWidth?: string;
	showActions?: boolean;
	popStateOnClose?: boolean;
	translations: Record<string, string | undefined>;
}>(), {
	minHeight: 'unset',
	maxWidth: 'unset',
	showActions: true,
	popStateOnClose: true,
});

const modal = ref<HTMLDivElement | null>(null);

const modalStyle = computed(() => ({
	minHeight: props.minHeight,
	maxWidth: props.maxWidth,
}));

const preventScroll = () => {
	document.body.style.overflow = 'hidden';
};

const restoreScroll = () => {
	document.body.style.overflow = 'auto';
};

const handleCloseModalAction = () => {
	restoreScroll();
	closeEcommerceModal();

	if (props.popStateOnClose) {
		const params = new URLSearchParams(window.location.search);

		if (!params.get('open-modal')) return;
		window.history.back();
	}
};

onMounted(() => {
	modal.value?.focus();
	if (document.body.style.overflow !== 'hidden') {
		preventScroll();
	}

	window.addEventListener('popstate', handleCloseModalAction);
});

onBeforeUnmount(() => {
	restoreScroll();

	window.removeEventListener('popstate', handleCloseModalAction);
});
</script>

<template>
	<Transition name="fade">
		<div
			v-show="true"
			class="modal-wrapper"
		>
			<div
				tabindex="-1"
				class="modal-backdrop"
				@click="handleCloseModalAction"
			/>
			<div
				ref="modal"
				:style="modalStyle"
				class="modal"
				tabindex="0"
				aria-modal="true"
				@keydown.esc="handleCloseModalAction"
			>
				<div class="modal__close-section-mobile">
					<button @click="handleCloseModalAction">
						<IconBack />
						<span class="text-bold-2">
							{{ translations.back || 'Back' }}
						</span>
					</button>
				</div>
				<CloseButton
					class="modal__close-button"
					@click="handleCloseModalAction"
				/>
				<div class="modal__content">
					<slot name="content" />
				</div>
				<div
					v-if="showActions"
					class="modal__actions"
				>
					<slot name="actions" />
				</div>
			</div>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";

.modal-wrapper {
	position: fixed;
	top: 50%;
	left: 50%;
	z-index: $z-index-site-engine-modal-overlay;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100vw;
	height: 100vh;
	transform: translate(-50%, -50%);

	@include site-engine-mobile {
		transform: none;
		height: 100dvh;
		top: 0;
		left: 0;
		padding-top: env(safe-area-inset-top);
		padding-bottom: env(safe-area-inset-bottom);
	}
}

.modal-backdrop {
	position: absolute;
	width: 100%;
	height: 100%;
	cursor: pointer;
	background-color: rgba($color-dark, 0.5);

	@include site-engine-mobile {
		height: 100dvh;
	}
}

.modal {
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	min-width: 540px;
	font-family: var(--font-primary);
	text-align: center;
	background-color: var(--color-light);
	outline: none;

	&__content {
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: 16px;

		@include site-engine-mobile {
			overflow-y: auto;
		}
	}

	&__close-button {
		position: absolute;
		top: 4px;
		right: 4px;
		z-index: 1;
		align-self: flex-end;

		@include site-engine-mobile {
			display: none;
		}
	}

	&__close-section-mobile {
		display: none;

		@include site-engine-mobile {
			display: flex;
			border-bottom: 1px solid $color-gray-border;
			justify-content: flex-start;
			padding: 16px;
			position: sticky;
			top: 0;
			background-color: var(--color-light);
			z-index: 1;

			button {
				align-items: center;
				display: flex;
				gap: 8px;
			}

			span {
				font-size: 16px;
				font-weight: 700;
			}
		}
	}

	&__actions {
		border-top: 1px solid $color-gray-border;
		display: flex;
		flex-direction: column;
		padding: 16px;
		width: 100%;

		@include site-engine-mobile {
			position: sticky;
			bottom: 0;
			background-color: var(--color-light);
			z-index: 1;
		}
	}

	@include site-engine-mobile {
		height: 100dvh;
		min-width: 100dvw;
	}
}
</style>
